import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SprintSortType } from '../../../redux/slices/resources';

interface SprintSortButtonProps {
  sortBy: SprintSortType;
  onSortChange: (sortBy: SprintSortType) => void;
}

const SprintSortButton: React.FC<SprintSortButtonProps> = ({
  sortBy,
  onSortChange,
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const sortOptions: Array<{ type: SprintSortType; label: string }> = [
    { type: 'latest', label: 'Latest Sprint' },
    { type: 'oldest', label: 'Oldest Sprint' },
    { type: 'name_asc', label: 'Name (A-Z)' },
    { type: 'name_desc', label: 'Name (Z-A)' },
  ];

  // Calculate menu position when opening
  useEffect(() => {
    if (showSortMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4, // 4px gap
        left: rect.right + window.scrollX - 192, // 192px is w-48 (menu width), align to right
      });
    } else {
      setMenuPosition(null);
    }
  }, [showSortMenu]);

  // Handle clicking outside to close sort menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        sortMenuRef.current && 
        !sortMenuRef.current.contains(target) &&
        buttonRef.current && 
        !buttonRef.current.contains(target)
      ) {
        setShowSortMenu(false);
        setMenuPosition(null);
      }
    };

    if (showSortMenu) {
      // Use click event (not capture phase) to allow menu item clicks to process first
      // The menu container's stopPropagation will prevent this from firing for clicks inside
      document.addEventListener('click', handleClickOutside);
      
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showSortMenu]);

  const handleSortSelect = (sortType: SprintSortType) => {
    onSortChange(sortType);
    setShowSortMenu(false);
    setMenuPosition(null);
  };

  const menuContent = showSortMenu && menuPosition && (
    <div
      className="fixed w-48 bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[10000] overflow-hidden"
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
      ref={sortMenuRef}
      onClick={(e) => {
        // Prevent clicks inside menu from bubbling to document
        e.stopPropagation();
      }}
    >
      <div className="py-1">
        {sortOptions.map((option) => {
          const isSelected = sortBy === option.type;
          return (
            <button
              key={option.type}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSortSelect(option.type);
              }}
              onMouseDown={(e) => {
                // Prevent mousedown from triggering outside click handler
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between ${
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500 dark:border-blue-400 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-tertiary-dark border-l-2 border-transparent'
              }`}
            >
              <span>{option.label}</span>
              {isSelected && (
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSortMenu(!showSortMenu);
          }}
          className={`p-2 rounded-md transition-colors ${
            showSortMenu
              ? 'bg-gray-100 dark:bg-tertiary-dark text-gray-700 dark:text-gray-200'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-tertiary-dark'
          }`}
          title="Sort sprints"
          aria-label="Sort sprints"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
        </button>
      </div>
      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
};

export default SprintSortButton;